/**
 * Multipack quantity selector (1 / 2 / 3 / 4 Stück).
 * When an option is selected, updates price, compare-at price, discount badge
 * AND the base price ("Grundpreis") in:
 *   - the main price block on the PDP (<product-price>)
 *   - the visible sticky add-to-cart price ([ref="stickyPriceContainer"])
 *   - the prices inside the sticky add-to-cart button ([ref="buttonPrice"] / [ref="buttonComparePrice"])
 * Each radio carries the pre-calculated values from blocks/custom-multipack-quantity.liquid
 * (data-variant-price, data-variant-sale-price, data-unit-price-text).
 */
class MultipackQuantitySelector extends HTMLElement {
  #boundSellingPlanChange;

  connectedCallback() {
    this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
    this.hiddenInput = this.querySelector('input[type="hidden"][name="quantity"]');
    this.selectedTitleEl = this.querySelector('.product__selected-variant-title');

    // Main price block
    this.priceBlock = document.querySelector('product-price');
    this.priceContainer = this.priceBlock?.querySelector('[ref="priceContainer"]') || this.priceBlock;

    // Sticky add-to-cart: visible price display + prices inside the button
    this.stickyPriceContainer = document.querySelector('[ref="stickyPriceContainer"]');
    this.stickyComparePriceEl = document.querySelector('[ref="buttonComparePrice"]');
    this.stickySalePriceEl = document.querySelector('[ref="buttonPrice"]');

    this.addEventListener('change', this.#onRadioChange.bind(this));

    this.#boundSellingPlanChange = this.#onSellingPlanChange.bind(this);
    document.addEventListener('change', this.#boundSellingPlanChange);

    const checked = this.radios.find((radio) => radio.checked);
    this.#applySelection(checked);
    this.#syncSubscriptionLock();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#boundSellingPlanChange);
  }

  #onRadioChange(event) {
    if (event.target.type !== 'radio') return;
    this.#applySelection(event.target);
  }

  #onSellingPlanChange(event) {
    if (event.target.name !== 'selling_plan') return;
    this.#syncSubscriptionLock();
  }

  #parsePrice(text) {
    if (!text) return 0;
    return parseFloat(text.replace('€', '').replace(/\./g, '').replace(',', '.').trim()) || 0;
  }

  #syncSubscriptionLock() {
    const sellingPlanRadios = Array.from(document.querySelectorAll('input[name="selling_plan"]'));
    const isSubscription = sellingPlanRadios.some((radio) => radio.checked && radio.value !== '');

    this.classList.toggle('multipack-quantity-selector--locked', isSubscription);

    this.radios.forEach((radio, index) => {
      const isFirstOption = index === 0;
      radio.disabled = isSubscription && !isFirstOption;
    });

    if (isSubscription) {
      const firstRadio = this.radios[0];
      if (firstRadio && !firstRadio.checked) {
        firstRadio.checked = true;
      }
      this.#applySelection(firstRadio);
    }
  }

  /* ---------- helpers that work on any price container (main or sticky) ---------- */

  #getCompareGroupEl(container) {
    const compareEl = container?.querySelector('.compare-at-price');
    return compareEl ? compareEl.closest('[role="group"]') : null;
  }

  #ensureCompareGroup(container) {
    let group = this.#getCompareGroupEl(container);
    if (group) return group;

    const salePriceEl = container?.querySelector('.price');
    if (!salePriceEl || !container) return null;

    const saleGroup = salePriceEl.closest('[role="group"]') || salePriceEl.parentElement;

    group = document.createElement('span');
    group.setAttribute('role', 'group');
    group.innerHTML = '<span class="visually-hidden">Normaler Preis&nbsp;</span><span class="compare-at-price"></span>';

    if (saleGroup && saleGroup.parentElement === container) {
      container.insertBefore(group, saleGroup);
    } else {
      container.insertBefore(group, container.firstChild);
    }

    return group;
  }

  #ensureTooltip(container) {
    if (!container) return null;
    let wrapper = container.querySelector('.custom-tooltip-wrapper');
    if (wrapper) return wrapper;

    wrapper = document.createElement('span');
    wrapper.className = 'custom-tooltip-wrapper';
    wrapper.innerHTML = '<h6 class="tooltiptext"></h6>';
    container.appendChild(wrapper);
    return wrapper;
  }

  /**
   * Base price wrapper (<small class="unit-price">) that belongs to a price container.
   * Main: inside <product-price>. Sticky: sibling of [ref="stickyPriceContainer"].
   */
  #getUnitPriceWrapper(container) {
    if (!container) return null;
    const scope = container.closest('product-price') || container.parentElement || container;
    return scope.querySelector('small.unit-price, .unit-price');
  }

  /**
   * Writes the base price text. Works with both structures:
   *  - theme default: <small class="unit-price"><span class="visually-hidden">…</span><span class="unit-price__value">…</span></small>
   *  - after assets/appstle-price-sync.js ran: the value span is replaced by a plain text node
   * Also updates Appstle's remembered original value, so switching back from
   * subscription to one-time purchase restores the correct (multipack) base price.
   */
  #setUnitPrice(wrapper, value) {
    if (!wrapper || !value) return;

    const valueEl = wrapper.querySelector('.unit-price__value');
    if (valueEl) {
      valueEl.textContent = value;
    } else {
      const hiddenLabel = wrapper.querySelector('.visually-hidden');
      wrapper.replaceChildren();
      if (hiddenLabel) wrapper.appendChild(hiddenLabel);
      const span = document.createElement('span');
      span.className = 'unit-price__value';
      span.textContent = value;
      wrapper.appendChild(span);
    }

    if (wrapper instanceof HTMLElement && wrapper.dataset.appstleOriginalUnitPrice) {
      wrapper.dataset.appstleOriginalUnitPrice = value;
    }
  }

  #updatePriceContainer(container, radio, { comparePriceValue, salePriceValue, hasDiscount, withTooltip }) {
    if (!container) return;

    const salePriceEl = container.querySelector('.price');
    if (salePriceEl) {
      salePriceEl.textContent = ' ' + (radio.dataset.variantSalePrice || '');
    }

    if (hasDiscount) {
      const group = this.#ensureCompareGroup(container);
      const compareEl = group?.querySelector('.compare-at-price');
      if (compareEl) compareEl.textContent = radio.dataset.variantPrice || '';
      if (group) group.style.display = '';

      if (withTooltip) {
        const tooltipWrapper = this.#ensureTooltip(container);
        const tooltipText = tooltipWrapper?.querySelector('.tooltiptext');
        if (tooltipText) {
          const realPercent = Math.round((1 - salePriceValue / comparePriceValue) * 100);
          tooltipText.textContent = `Spare ${realPercent}%`;
        }
        if (tooltipWrapper) tooltipWrapper.style.display = '';
      }
    } else {
      const group = this.#getCompareGroupEl(container);
      if (group) group.style.display = 'none';

      const tooltipWrapper = container.querySelector('.custom-tooltip-wrapper');
      if (tooltipWrapper) tooltipWrapper.style.display = 'none';
    }

    // Base price ("Grundpreis") for the selected pack
    if (radio.dataset.unitPriceText) {
      this.#setUnitPrice(this.#getUnitPriceWrapper(container), radio.dataset.unitPriceText);
    }
  }

  #applySelection(radio) {
    if (!radio) return;

    if (this.hiddenInput) {
      this.hiddenInput.value = radio.dataset.packQuantity;
    }

    const comparePriceValue = this.#parsePrice(radio.dataset.variantPrice);
    const salePriceValue = this.#parsePrice(radio.dataset.variantSalePrice);
    const hasDiscount = comparePriceValue > salePriceValue;
    const prices = { comparePriceValue, salePriceValue, hasDiscount };

    // Main price block (with "Spare X%" badge)
    this.#updatePriceContainer(this.priceContainer, radio, { ...prices, withTooltip: true });

    // Visible sticky add-to-cart price (only adds a badge if the sticky already shows one)
    const stickyHasTooltip = !!this.stickyPriceContainer?.querySelector('.custom-tooltip-wrapper');
    this.#updatePriceContainer(this.stickyPriceContainer, radio, { ...prices, withTooltip: stickyHasTooltip });

    // Prices inside the sticky add-to-cart button
    if (this.stickySalePriceEl) {
      this.stickySalePriceEl.textContent = radio.dataset.variantSalePrice || '';
    }

    if (this.stickyComparePriceEl) {
      if (hasDiscount) {
        this.stickyComparePriceEl.textContent = radio.dataset.variantPrice || '';
        this.stickyComparePriceEl.style.display = '';
      } else {
        this.stickyComparePriceEl.style.display = 'none';
      }
    }

    if (this.selectedTitleEl && radio.dataset.optionName) {
      this.selectedTitleEl.textContent = radio.dataset.optionName;
    }
  }
}

if (!customElements.get('multipack-quantity-selector')) {
  customElements.define('multipack-quantity-selector', MultipackQuantitySelector);
}
